import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Printer, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdmitCardsTab = ({
  exams,
  classes,
  selectedExam,
  selectedClass,
  admitCards,
  getAuthHeader,
  onSelectExam,
  onSelectClass,
  onGenerateCards,
  onPrintCard,
  onClearCards
}) => {
  const handleClearCards = async () => {
    try {
      await axios.post(
        `${API_URL}/api/exam-management/admit-cards/bulk-save`,
        { exam_id: selectedExam?.id, class_id: selectedClass, cards: [] },
        { headers: getAuthHeader() }
      );
      onClearCards();
      toast.info('Cleared old cards. Click Generate to create new ones.');
    } catch (e) {
      toast.error('Failed to clear cards');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">🎫 Admit Card Generation</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Generate Admit Cards</CardTitle>
          <CardDescription>Select exam and class to generate admit cards for students</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Select Exam</Label>
              <Select value={selectedExam?.id || ''} onValueChange={onSelectExam}>
                <SelectTrigger><SelectValue placeholder="Select Exam" /></SelectTrigger>
                <SelectContent>
                  {exams.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Select Class</Label>
              <Select value={selectedClass} onValueChange={onSelectClass}>
                <SelectTrigger><SelectValue placeholder="Select Class" /></SelectTrigger>
                <SelectContent>
                  {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={onGenerateCards} 
                disabled={!selectedExam || !selectedClass}
                className="w-full"
              >
                <Printer className="h-4 w-4 mr-2" /> Generate Admit Cards
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {admitCards.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Generated Admit Cards ({admitCards.length})</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Cards are saved and will persist</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleClearCards}
                >
                  <RefreshCw className="h-4 w-4 mr-1" /> Regenerate
                </Button>
                <Button variant="outline" onClick={() => admitCards.forEach(c => onPrintCard(c))}>
                  <Printer className="h-4 w-4 mr-2" /> Print All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {admitCards.map(card => (
                <Card key={card.student_id || card.id} className="bg-muted/30">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{card.student_name}</p>
                        <p className="text-sm text-muted-foreground">Roll: {card.roll_number}</p>
                        <p className="text-xs text-muted-foreground mt-1">{card.admit_card_number}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => onPrintCard(card)}>
                        <Printer className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdmitCardsTab;
